// `CompileSource` (SuiteCompile/Pipeline.swift): everything compile reads from a project, captured once so rendering
// never touches live documents — from the live documents (`live`) or exactly as a checkpoint recorded them (`at`,
// the project and each fragment read at its heads). Collections' members are evaluated here too, by the core's
// `SavedSearch` (`savedSearchesOf`, `searchMembers`: `DocumentStore.members(of:in:)`), from the documents rather than the
// Mac's index.
import * as A from '@automerge/automerge';
import {
  checkpointHeadsIn, decodePublication, isMusicalOf, emptyPublication, encodeHeads, firstPostNumberOf, MergeTags, orderedFragmentIDs, readFragment, readProject,
  ROOT, SwiftReader, swiftCompare, trimCFWhitespace, type CheckpointRecord, type FragmentKind, type MergeTagStyle, type MetadataField, type MetadataValue,
  type ProjectRecord, type PublicationDetails, type StoredArrangement, type StoryEntity,
} from '@writing-suite/core';
import { displayString, isNamed, savedSearchesOf, searchMembers as membersOf, type SavedSearch } from '@writing-suite/core/plan';
import type { AudiobookSettings, PenName } from './types.ts';

export interface SourceFragment {
  id: string;
  title: string;
  kind: FragmentKind;
  text: string;
  metadata: Record<string, MetadataValue>;
  includeInCompile: boolean;
}

export interface CompileSource {
  title: string;
  /** Replaces the title-derived short title for running heads. */
  shortTitle: string | null;
  language: string;
  projectID: string;
  projectType: 'manuscript' | 'screenplay' | 'journal';
  arrangements: StoredArrangement[];
  fields: MetadataField[];
  fragments: Map<string, SourceFragment>;
  audiobook: AudiobookSettings;
  publication: PublicationDetails;
  mergeTags: MergeTagStyle[];
  firstPostNumber: number | null;
  savedSearches: SavedSearch[];
  /** The cast's characters, for a play's Cast of Characters page when the writer hasn't written one. */
  characters?: StoryEntity[];
  /** A play that's a musical (`ProjectDocument.isMusical`). */
  musical?: boolean;
  /** Members of each saved search, by search id. */
  searchMembers: Map<string, Set<string>>;
  /** Only these fragments compile (Publish with the contents' filter on); the folders above them stay as headings, as
   *  with `select.include`, whose conditions they must match too. */
  limit?: ReadonlySet<string> | null;
  /** The project's pen name (`settings.penName`), for `AuthorInfo.publishing`. */
  penName: PenName | null;
  /** Heads (hex) of each document as read, so `matches` can tell whether this is exactly a checkpoint's state. */
  projectHeads: string;
  fragmentHeads: Record<string, string>;
  /** A checkpoint's fragments whose history hasn't reached this device (left out rather than guessed at). */
  missingFragments: string[];
}

const FRAGMENT = 'fragment.';

// MARK: Settings

/** `ProjectDocument.audiobookSettings()`: `settings.audiobook` (JSON, tolerant) and the per-word pronunciations. */
export function audiobookSettingsOf(settings: Record<string, unknown>): AudiobookSettings {
  const result: AudiobookSettings = { narrator: '', engine: 'system', pronunciations: {} };
  const raw = settings.audiobook;
  if (typeof raw === 'string') {
    try {
      const o = JSON.parse(raw) as Record<string, unknown>;
      if (typeof o.narrator === 'string') result.narrator = o.narrator;
      if (o.engine === 'system' || o.engine === 'elevenlabs') result.engine = o.engine;
      for (const k of ['systemVoice', 'elevenLabsVoiceID', 'elevenLabsVoiceName', 'elevenLabsModel'] as const) {
        if (typeof o[k] === 'string') result[k] = o[k] as string;
      }
    } catch { /* the defaults, as the Swift's `try?` */ }
  }
  const p = settings.pronunciations;
  if (p && typeof p === 'object') for (const [k, v] of Object.entries(p as Record<string, unknown>)) if (typeof v === 'string') result.pronunciations[k] = v;
  return result;
}

function json(settings: Record<string, unknown>, key: string): unknown {
  const raw = settings[key];
  if (typeof raw !== 'string' || raw === '') return undefined;
  try { return JSON.parse(raw); } catch { return undefined; }
}

/** `ProjectDocument.penName()`. */
export function penNameOf(settings: Record<string, unknown>): PenName | null {
  const o = json(settings, 'penName') as Record<string, unknown> | undefined;
  if (!o || typeof o.name !== 'string') return null;
  const contact = Array.isArray(o.contact) ? o.contact.filter((l): l is string => typeof l === 'string') : undefined;
  return {
    id: typeof o.id === 'string' ? o.id : '', name: o.name, ...(typeof o.surname === 'string' ? { surname: o.surname } : {}),
    ...(contact !== undefined ? { contact } : {}),
  };
}

/** `ProjectDocument.publicationDetails()`. */
export function publicationOf(settings: Record<string, unknown>): PublicationDetails {
  const o = json(settings, 'publication');
  return o === undefined ? emptyPublication() : decodePublication(o);
}


// MARK: Reading the documents

function fromRecord(record: ProjectRecord, fragments: Map<string, SourceFragment>, docs: Map<string, A.Doc<unknown>>,
  now: Date, searchMembers: Record<string, Iterable<string>> | undefined, live: () => ProjectRecord): CompileSource {
  const settings = record.settings;
  const savedSearches = savedSearchesOf(settings);
  const members = new Map<string, Set<string>>();
  if (searchMembers) {
    for (const [id, list] of Object.entries(searchMembers)) members.set(id, new Set(list));
  } else {
    // Collections are evaluated live (the Mac's index knows only the present).
    if (savedSearches.length) for (const [id, list] of membersOf(docs, live(), savedSearches, now)) members.set(id, new Set(list));
  }
  return {
    title: record.title, shortTitle: null, language: record.locale, projectID: record.id, projectType: record.projectType,
    arrangements: record.arrangements, fields: record.fields, fragments,
    audiobook: audiobookSettingsOf(settings), publication: publicationOf(settings),
    mergeTags: MergeTags.stylesOf(settings.mergeTags, record.projectType === 'journal'),
    firstPostNumber: firstPostNumberOf(settings), savedSearches, searchMembers: members, penName: penNameOf(settings),
    characters: record.entities.filter((e) => e.kind === 'character'),
    musical: isMusicalOf(record.projectType, settings),
    projectHeads: '', fragmentHeads: {}, missingFragments: [],
  };
}

const sourceFragment = (id: string, doc: A.Doc<unknown>, heads?: string[]): SourceFragment => {
  const f = readFragment(doc, heads);
  return { id, title: f.title, kind: f.kind, text: f.text, metadata: f.metadata, includeInCompile: f.includeInCompile };
};

/** A map's document (its kind as it is now). */
const isMap = (doc: A.Doc<unknown>) => new SwiftReader(doc).string(ROOT, 'kind') === 'map';

export interface SourceOptions {
  /** Evaluate saved searches as of this time (`modified within`); defaults to now. */
  now?: Date;
  /** Members of saved searches as the app already knows them (search id → fragment ids); computed when absent. */
  searchMembers?: Record<string, Iterable<string>>;
}

/** `CompileSource.live`: the documents as they are. */
export function liveSource(docs: Map<string, A.Doc<unknown>>, o: SourceOptions = {}): CompileSource {
  const project = docs.get('project');
  if (!project) throw new Error('No project document');
  const record = readProject(project);
  const fragments = new Map<string, SourceFragment>();
  const fragmentHeads: Record<string, string> = {};
  for (const [name, doc] of docs) {
    if (!name.startsWith(FRAGMENT)) continue;
    const id = name.slice(FRAGMENT.length);
    fragmentHeads[id] = encodeHeads(A.getHeads(doc));
    // A map never compiles (it can't be in the manuscript, and a collection reaching one gets nothing from it).
    if (isMap(doc)) continue;
    fragments.set(id, sourceFragment(id, doc));
  }
  const source = fromRecord(record, fragments, docs, o.now ?? new Date(), o.searchMembers, () => record);
  source.projectHeads = encodeHeads(A.getHeads(project));
  source.fragmentHeads = fragmentHeads;
  return source;
}

/** `CompileSource.at`: the project exactly as `checkpoint` recorded it. Collections are evaluated live, as the Mac's
 *  index knows only the present. A fragment whose history hasn't arrived is listed in `missingFragments`. */
export function sourceAt(docs: Map<string, A.Doc<unknown>>, checkpoint: CheckpointRecord, o: SourceOptions = {}): CompileSource {
  const project = docs.get('project');
  if (!project) throw new Error('No project document');
  const heads = checkpointHeadsIn(checkpoint.projectHeads, project);
  if (!heads) throw new Error(`The project as it was at “${checkpoint.name}” hasn’t reached this device yet.`);
  const record = readProject(project, heads);
  const fragments = new Map<string, SourceFragment>();
  const missing: string[] = [];
  for (const [id, hex] of Object.entries(checkpoint.fragmentHeads)) {
    const doc = docs.get(FRAGMENT + id);
    if (doc && isMap(doc)) continue;
    const at = doc ? checkpointHeadsIn(hex, doc) : null;
    if (!doc || !at) { missing.push(id); continue; }
    fragments.set(id, sourceFragment(id, doc, at));
  }
  const source = fromRecord(record, fragments, docs, o.now ?? new Date(), o.searchMembers, () => readProject(project));
  source.projectHeads = checkpoint.projectHeads;
  source.fragmentHeads = { ...checkpoint.fragmentHeads };
  source.missingFragments = missing.sort(swiftCompare);
  return source;
}

/** `matches(_:)`: whether this was read at exactly the checkpoint's heads. */
export function sourceMatches(source: CompileSource, checkpoint: CheckpointRecord): boolean {
  if (source.projectHeads === '' || source.projectHeads !== checkpoint.projectHeads) return false;
  const a = source.fragmentHeads, b = checkpoint.fragmentHeads;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k]);
}

/** `collectionMembers`: a saved search's fragments by name or id, else a hand-picked collection's; null for neither. */
export function collectionMembers(source: CompileSource, name: string): Set<string> | null {
  const search = source.savedSearches.find((s) => isNamed(s, name));
  if (search) return source.searchMembers.get(search.id) ?? new Set();
  const wanted = trimCFWhitespace(name);
  const list = source.arrangements.find((a) => a.role === 'collection' && (a.id === wanted || a.name.toLowerCase() === wanted.toLowerCase()));
  return list ? new Set(orderedFragmentIDs(list)) : null;
}

/** `display`: a metadata value as its display name (enum options resolve to their names). */
export function displayValue(source: CompileSource, fragment: SourceFragment, field: string): string | null {
  const value = fragment.metadata[field];
  if (!value) return null;
  if (value.type === 'text') {
    const option = source.fields.find((f) => f.id === field)?.options.find((o) => o.id === value.value);
    if (option) return option.name;
  }
  return displayString(value);
}

/** A hand-made source (tests, previews): the Swift's memberwise `init(title:…)`. */
export function makeSource(o: { title: string; shortTitle?: string | null; language: string; projectID: string;
  arrangements: StoredArrangement[]; fields?: MetadataField[]; fragments: SourceFragment[] | Map<string, SourceFragment>;
  projectType?: CompileSource['projectType'] }): CompileSource {
  return {
    title: o.title, shortTitle: o.shortTitle ?? null, language: o.language, projectID: o.projectID, projectType: o.projectType ?? 'manuscript',
    arrangements: o.arrangements, fields: o.fields ?? [],
    fragments: o.fragments instanceof Map ? o.fragments : new Map(o.fragments.map((f) => [f.id, f])),
    audiobook: { narrator: '', engine: 'system', pronunciations: {} }, publication: emptyPublication(), mergeTags: [],
    firstPostNumber: null, savedSearches: [], searchMembers: new Map(), penName: null, projectHeads: '', fragmentHeads: {}, missingFragments: [],
  };
}
